import { inject, Injectable } from '@angular/core';
import { environment } from 'oneok/environments/environment';
import { StorageService } from '@core/services';
import { Account } from '@legacy-models/portal/Account';
import { DownloadRequest } from '@legacy-models/portal/Download';
import { BehaviorSubject, catchError, map, race, single, Subscription, throwError, timer } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { saveAs } from 'file-saver';
import posthog from 'posthog-js';
import { DOCUMENT } from '@angular/common';

interface TemporaryDownloadResponse {
  headers: HttpHeaders;
  body: null;
  status: 202;
  isTemporary: true;
}

@Injectable({
  providedIn: 'root'
})
export class Download {
  private readonly _baseUrl: string = environment.apiUrlV2;
  private _accountId: string;
  private readonly _http: HttpClient = inject(HttpClient);
  private readonly _storageSvc: StorageService = inject(StorageService);
  private readonly _document: Document = inject(DOCUMENT);
  private readonly _requestsSource = new BehaviorSubject<DownloadRequest[]>([]);
  requests$ = this._requestsSource.asObservable();
  private readonly _backgroundSubscriptions = new Map<string, Subscription>();

  constructor() {
    this._storageSvc.currentAccount$.subscribe(this._onCurrentAccountSubscription.bind(this));
  }

  /** Download Flow Explanation
   * // 1. User clicks download (invoices.service.ts)
  submitDownload(invoiceSummary) → _downloadSvc.addRequest()
  
  // 2. Download starts (download.service.ts)  
  addRequest() → _executeRequest() → _doNggpInvoiceDownload()
  
  // 3. Two scenarios:
  //    a) Fast response (<30s): Normal download flow
  //    b) Slow response (>30s): Show waiting message + background processing
  
  // 4. User can navigate anywhere, download completes automatically
  // 5. If user cancels: clearDownload() properly cleans up subscriptions
   */

  /**
   * Method receives a new `DownloadRequest` object,
   *   and both caches and executes it
   * @param downloadRequest `DownloadRequest`
   */
  addRequest(downloadRequest: DownloadRequest): void {
    downloadRequest.isInProgress = true;
    const UPDATED_REQUESTS = [...this._requestsSource.getValue(), downloadRequest];
    this._requestsSource.next(UPDATED_REQUESTS);
    this._executeRequest(downloadRequest);
  }

  /**
   * Method receives a `DownloadRequest` ID,
   *   and clears it from the cache
   * @param downloadId unique download identifier
   */
  clearRequests(downloadIds: string[]): void {
    // Clean up any background subscriptions for these requests
    downloadIds.forEach(id => {
      const subscription = this._backgroundSubscriptions.get(id);
      if (subscription) {
        subscription.unsubscribe();
        this._backgroundSubscriptions.delete(id);
      }
    });

    this._requestsSource.next(this._requestsSource.getValue().filter(({ id }) => !downloadIds.includes(id)));
  }

  /**
   * Method does the actual _requesting_ of the (downloadable) resource
   *   i.e. fires an HTTP Request
   * @param downloadRequest `DownloadRequest`
   */
  private _executeRequest(downloadRequest: DownloadRequest): void {
    const { type: DOWNLOAD_TYPE } = downloadRequest;
    const SUBSCRIPTION_OPTS = {
      next: this._onCompletedRequest.bind(this, downloadRequest),
      error: this._onFailedRequest.bind(this, downloadRequest)
    };
    /**
     * Function handles the plumbing for firing HTTP request for
     *   downloading NGGP Invoice
     * @returns Subscription
     */
    const doNggpInvoiceDownload = (): Subscription => {
      const {
        apiParams: { accountingDate: ACCOUNTING_DATE, contractNum: CONTRACT_NUM }
      } = downloadRequest;
      // Create the HTTP request observable
      const httpRequest$ = this._http
        .get(
          `${this._baseUrl}/invoice/accounts/${this._accountId}/summarypdf/${ACCOUNTING_DATE}?ContractNumber=${CONTRACT_NUM}&InvoiceType=GATH`,
          {
            observe: 'response',
            responseType: 'blob'
          }
        )
        .pipe(
          single(),
          catchError((error: HttpErrorResponse) => throwError(() => new Error(error.message)))
        ); // Create a timer for 30 seconds
      const timeoutTimer$ = timer(30000).pipe(
        map(() => {
          // Create a temporary waiting response
          return {
            headers: new HttpHeaders(),
            body: null,
            status: 202, // Accepted - processing in progress
            isTemporary: true
          } as TemporaryDownloadResponse;
        })
      ); // Race between the HTTP request and the 30-second timer
      const raceResult$ = race(httpRequest$, timeoutTimer$);

      return raceResult$.subscribe({
        next: (response: HttpResponse<Blob> | TemporaryDownloadResponse) => {
          if ('isTemporary' in response) {
            // Send temporary response to indicate processing is still ongoing
            const tempDownloadRequest = { ...downloadRequest };
            tempDownloadRequest.isInProgress = true;
            tempDownloadRequest.waitingMessage =
              'Your download is taking longer than expected. You can continue to use the site while we are still processing your request in the background.';

            // Continue waiting for the actual response in the background
            const backgroundSub = httpRequest$.subscribe({
              next: actualResponse => {
                SUBSCRIPTION_OPTS.next(actualResponse);
                // Clean up stored subscription
                this._backgroundSubscriptions.delete(downloadRequest.id);
              },
              error: error => {
                SUBSCRIPTION_OPTS.error(error);
                // Clean up stored subscription
                this._backgroundSubscriptions.delete(downloadRequest.id);
              }
            });

            // Store subscription for persistence across navigation
            this._backgroundSubscriptions.set(downloadRequest.id, backgroundSub);

            // Update UI to show waiting message
            this._requestsSource.next(
              this._requestsSource.getValue().map(request => {
                if (request.id === downloadRequest.id) {
                  request.waitingMessage =
                    'Your download is taking longer than expected. You can continue to use the site while we are still processing your request in the background.';
                }
                return request;
              })
            );
          } else {
            // Normal response received within 30 seconds
            SUBSCRIPTION_OPTS.next(response);
            // Clean up any stored subscription since request completed normally
            this._backgroundSubscriptions.delete(downloadRequest.id);
          }
        },
        error: error => SUBSCRIPTION_OPTS.error(error)
      });
    };
    switch (DOWNLOAD_TYPE) {
      case 'NGGP Invoice':
        doNggpInvoiceDownload();
        break;
    }
  }

  /**
   * Method is executed when a download completes **successfully**
   * @param response any
   * @example
   * <PDF_Content> (would have Content-Type Header of 'application/octet-stream')
   */
  private _onCompletedRequest(
    downloadRequest: DownloadRequest,
    { headers, body: response, status: httpCode }: HttpResponse<Blob>
  ): void {
    // Clean up any background subscription
    this._backgroundSubscriptions.delete(downloadRequest.id);

    if (httpCode !== 200) {
      return;
    }
    const { type: DOWNLOAD_TYPE, id: DOWNLOAD_ID, apiParams } = downloadRequest;
    const isNggpInvoice = DOWNLOAD_TYPE === 'NGGP Invoice';
    /**
     * Function completes the logic loop if/when the actual
     *   PDF _file_ is downloaded from the browser
     */
    const completeRequest = (): void => {
      // Check if user is currently on the summary page viewing this download
      const isOnSummaryPage = !!this._document.querySelector('nggp-invoices invoice-summary');

      this._requestsSource.next(
        this._requestsSource.getValue().map(request => {
          if (request.id === DOWNLOAD_ID) {
            request.isInProgress = false;
            // Clear waiting message when download completes successfully
            request.waitingMessage = undefined;
            // Set success message
            request.successMessage = 'Your PDF invoice has been downloaded successfully!';
            // Mark if user was present to see the completion
            request.userWasPresent = isOnSummaryPage;
          }
          return request;
        })
      );

      // // Auto-dismiss success message after 5 seconds
      // setTimeout(() => {
      //   this._requestsSource.next(
      //     this._requestsSource.getValue().map<DownloadRequest>(
      //       request => {
      //         if( request.id === DOWNLOAD_ID && request.successMessage ){
      //           request.successMessage = undefined;
      //         }
      //         return request
      //       }
      //     )
      //   );
      // }, 5000);
    };
    if (isNggpInvoice) {
      const { accountingDate: ACCOUNTING_DATE, contractNum: CONTRACT_NUM, invoiceNum: INVOICE_NUM } = apiParams;
      const isPdf = headers.get('Content-Type')!.includes('application/pdf');
      if (isPdf) {
        const FILENAME = `invoice--${CONTRACT_NUM}-${String(INVOICE_NUM).replaceAll('-', '')}`;
        saveAs(new Blob([response!], { type: 'application/pdf' }), FILENAME);
        posthog.capture('invoices:summary_download_detail_pdf_generate', {
          accounting_date: new Date(ACCOUNTING_DATE).toISOString(),
          contract_number: CONTRACT_NUM,
          invoice_number: INVOICE_NUM
        });
        completeRequest();
      } else {
        const errorResponse = response as unknown as { ResponseStatus?: { Message?: string } };
        this._onFailedRequest(downloadRequest, {
          message: errorResponse.ResponseStatus?.Message ?? 'The invoice download failed.'
        } as HttpErrorResponse);
      }
    }
  }

  /**
   * Callback in use for monitoring if/when the `currentAccount$`
   *   Observable changes
   * @param account `Account`
   */
  private _onCurrentAccountSubscription(account: Account): void {
    if (!account) {
      return;
    }
    this._accountId = account.AccountId;
  }

  /**
   *
   * @param `DownloadRequest`
   * @param error HttpErrorResponse
   */
  private _onFailedRequest({ id: failedDownloadId }: DownloadRequest, error: HttpErrorResponse): void {
    // Clean up any background subscription
    this._backgroundSubscriptions.delete(failedDownloadId);

    this._requestsSource.next(
      this._requestsSource.getValue().map(request => {
        if (request.id === failedDownloadId) {
          request.isInProgress = false;
          request.errorMessage = error.message;
          // Clear waiting message when download fails
          request.waitingMessage = undefined;
          // Clear success message when download fails
          request.successMessage = undefined;
        }
        return request;
      })
    );
  }
}
